import { useRef, useState } from 'react';
import { Alert, Anchor, Button, Checkbox, Group, Modal, Paper, Stack, Text, Textarea } from '@mantine/core';
import { IconMessageQuestion } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { getProductSurveyOptions, validateProductSurveyAnswer } from '@washday/utilities';
import type { ParticipateProductSurveyDto, ProductSurvey } from 'washday-sdk';
import { useActiveProductSurvey } from '../../hooks/productSurveys/queries';
import { useParticipateProductSurvey } from '../../hooks/productSurveys/mutations';

export function ActiveProductSurveyPrompt() {
  const { data, sessionKey } = useActiveProductSurvey();
  const campaignId = useRef(data?._id);
  if (data) campaignId.current = data._id;
  // Keep the success modal when cache clears, but reset drafts/completion for a new campaign.
  return <SurveyPrompt key={`${sessionKey}:${campaignId.current ?? 'none'}`} survey={data ?? null} />;
}

function SurveyPrompt({ survey }: { survey: ProductSurvey | null }) {
  const { t, ready } = useTranslation('ProductSurveys', { useSuspense: false });
  const [selected, setSelected] = useState<ProductSurvey | null>(null);
  const [opened, setOpened] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [selections, setSelections] = useState<string[]>([]);
  const [otherText, setOtherText] = useState('');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const lock = useRef(false);
  const { mutateAsync, isPending } = useParticipateProductSurvey();
  const busy = submitting || isPending;
  const active = selected ?? survey;
  const issue = active ? validateProductSurveyAnswer(active, { selections, otherText, comment }) : 'required';

  const participate = async (surveyId: string, dto: ParticipateProductSurveyDto) => {
    if (lock.current || busy) return;
    lock.current = true; setSubmitting(true); setError(false);
    try {
      const result = await mutateAsync({ surveyId, dto });
      setCompleted(true);
      setSuccess(result.status === 'answered');
      if (result.status === 'dismissed') setOpened(false);
    } catch { setError(true); }
    finally { lock.current = false; setSubmitting(false); }
  };
  const open = () => {
    if (!survey) return;
    if (selected?._id !== survey._id) { setSelections([]); setOtherText(''); setComment(''); setError(false); }
    setSelected(survey); setOpened(true);
  };

  if (!ready) return null;

  return <>
    {survey && !completed && <Paper withBorder radius="md" p="xs" mt="xs" style={{ minWidth: 0 }}>
      <Stack gap={6}>
        <Group gap={6} wrap="nowrap"><IconMessageQuestion size={17} style={{ flexShrink: 0 }} aria-hidden="true" />
          <Text size="xs" fw={600}>{t('invitation')}</Text></Group>
        <Text size="xs" c="dimmed" lineClamp={2} title={survey.description || survey.title}>{survey.description || survey.title}</Text>
        <Button size="compact-xs" fullWidth onClick={open} disabled={busy}>{t('respond')}</Button>
        <Anchor component="button" type="button" size="xs" disabled={busy}
          onClick={() => void participate(survey._id, { action: 'dismiss', source: 'web-pos' })}>{t('dismiss')}</Anchor>
        {error && !opened && <Text size="xs" c="red" role="alert">{t('error')}</Text>}
      </Stack>
    </Paper>}
    <Modal opened={opened} onClose={() => setOpened(false)} title={active?.title} centered size={540}
      closeButtonProps={{ 'aria-label': t('close') }} keepMounted styles={{ content: { maxWidth: 'calc(100vw - 24px)' } }}>
      {success ? <Alert color="green" role="status">{t('success')}</Alert> : active &&
        <form onSubmit={(event) => { event.preventDefault(); if (!issue) void participate(active._id, { action: 'answer', source: 'web-pos', selections,
          ...(selections.includes('other') ? { otherText: otherText.trim() } : {}), ...(comment.trim() ? { comment: comment.trim() } : {}),
        }); }}>
          <Stack gap="md">
            {active.description && <Text size="sm" c="dimmed">{active.description}</Text>}
            {error && <Alert color="red" role="alert">{t('error')}</Alert>}
            <Checkbox.Group label={active.question} description={active.questionHint} value={selections} onChange={(values) => { setSelections(values); if (!values.includes('other')) setOtherText(''); }}>
              <Stack gap="sm" mt="sm">
                {getProductSurveyOptions(active, active.otherLabel || t('other')).map((option) => <Checkbox key={option.key} value={option.key} label={option.label} disabled={busy} />)}
              </Stack>
            </Checkbox.Group>
            {selections.includes('other') && <Textarea label={active.otherTextPrompt || t('otherText')} placeholder={active.otherTextPlaceholder} value={otherText} required maxLength={1000}
              onChange={(event) => setOtherText(event.currentTarget.value)} autosize minRows={2} disabled={busy}
              error={issue === 'otherRequired' || issue === 'otherTooLong' ? t(issue) : undefined} />}
            {active.freeTextPrompt && <Textarea label={active.freeTextPrompt} placeholder={active.freeTextPlaceholder} value={comment} maxLength={2000}
              onChange={(event) => setComment(event.currentTarget.value)} autosize minRows={3} maxRows={8} disabled={busy} />}
            <Text size="xs" c="dimmed">{t('answerHint')}</Text>
            <Button type="submit" loading={busy} disabled={busy || Boolean(issue)}>{t('submit')}</Button>
          </Stack>
        </form>}
    </Modal>
  </>;
}
